/**
 * PropertyPages — cross-source per-URL view.
 *
 * Anchor: latest Frog crawl_pages. Enriched with GSC clicks +
 * impressions + position, GA4 sessions + bounce, DFS keywords + ETV
 * + backlinks. The single most useful daily-driver dev surface — pick
 * a URL, see everything across sources at a glance.
 */
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@tremor/react";
import {
  BookOpen, Search, ChevronUp, ChevronDown, ExternalLink,
} from "lucide-react";
import {
  useProperty, usePropertyPages,
  type PageRow, type PagesLensFilters,
} from "@/lib/queries";
import { cn } from "@/lib/utils";


export default function PropertyPages() {
  const { propertyId: rawId } = useParams<{ propertyId: string }>();
  const propertyId = Number(rawId);
  const property = useProperty(propertyId);
  const [filters, setFilters] = useState<PagesLensFilters>({
    sort: "inlinks", sort_dir: "desc", limit: 100, offset: 0,
  });

  const pages = usePropertyPages(propertyId, filters);

  const setF = (patch: Partial<PagesLensFilters>) =>
    setFilters((f) => ({ ...f, offset: 0, ...patch }));

  if (property.isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!property.data) return <div className="text-sm text-red-600">Property not found.</div>;
  const p = property.data;

  const sources = pages.data?.sources_present;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-semibold tracking-tight">Pages</h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Cross-source per-URL view of <span className="font-mono">{p.domain}</span>.
          Anchored on the latest Frog crawl + enriched with GSC search data,
          GA4 sessions, DFS keywords + backlinks. Click a row to expand.
        </p>
      </div>

      {sources && (
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          <span className="text-muted-foreground">Joined sources:</span>
          <SrcChip label="Frog crawl" present={sources.crawl} />
          <SrcChip label="GSC" present={sources.gsc} />
          <SrcChip label="GA4" present={sources.ga4} />
          <SrcChip label="DataForSEO" present={sources.dfs} />
        </div>
      )}

      <Card>
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input type="text" placeholder="filter URL by substring…"
                value={filters.search ?? ""}
                onChange={(e) => setF({ search: e.target.value || undefined })}
                className="w-full rounded-md border border-input bg-background pl-7 pr-2 py-1.5 text-xs" />
            </div>
            <select value={filters.status_class ?? ""}
              onChange={(e) => setF({ status_class: (e.target.value || undefined) as PagesLensFilters["status_class"] })}
              className="rounded-md border border-input bg-background px-2 py-1.5 text-xs">
              <option value="">All statuses</option>
              <option value="2xx">2xx</option>
              <option value="3xx">3xx</option>
              <option value="4xx">4xx</option>
              <option value="5xx">5xx</option>
            </select>
            <FilterToggle label="Title"
              value={filters.missing_title} onTrue="missing" onFalse="present"
              onChange={(v) => setF({ missing_title: v })} />
            <FilterToggle label="Meta"
              value={filters.missing_meta} onTrue="missing" onFalse="present"
              onChange={(v) => setF({ missing_meta: v })} />
            <FilterToggle label="GSC clicks"
              value={filters.has_gsc_clicks} onTrue=">0" onFalse="0"
              onChange={(v) => setF({ has_gsc_clicks: v })} />
            <select value={filters.indexability ?? ""}
              onChange={(e) => setF({ indexability: e.target.value || undefined })}
              className="rounded-md border border-input bg-background px-2 py-1.5 text-xs">
              <option value="">All indexability</option>
              <option value="Indexable">Indexable</option>
              <option value="Non-Indexable">Non-Indexable</option>
            </select>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            {pages.data && <span>{pages.data.items.length.toLocaleString()} of {pages.data.total.toLocaleString()} pages</span>}
            {Object.keys(filters).some((k) => !["sort", "sort_dir", "limit", "offset"].includes(k) && filters[k as keyof PagesLensFilters] !== undefined) && (
              <button type="button"
                onClick={() => setFilters({ sort: "inlinks", sort_dir: "desc", limit: 100, offset: 0 })}
                className="text-primary hover:underline">Clear filters</button>
            )}
          </div>
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="border-b border-border text-left">
            <tr>
              <SortHeader label="URL"            col="url"             filters={filters} setF={setF} className="w-[24%]" />
              <SortHeader label="Status"         col="status"          filters={filters} setF={setF} align="right" />
              <SortHeader label="Words"          col="word_count"      filters={filters} setF={setF} align="right" />
              <SortHeader label="In/Out"         col="inlinks"         filters={filters} setF={setF} align="right" />
              <SortHeader label="GSC clicks"    col="gsc_clicks"      filters={filters} setF={setF} align="right" />
              <SortHeader label="GSC impr"      col="gsc_impressions" filters={filters} setF={setF} align="right" />
              <SortHeader label="GSC pos"       col="gsc_clicks"      filters={filters} setF={setF} align="right" />
              <SortHeader label="GA4 sess"      col="ga4_sessions"    filters={filters} setF={setF} align="right" />
              <SortHeader label="GA4 bounce"    col="ga4_sessions"    filters={filters} setF={setF} align="right" />
              <SortHeader label="DFS kw"        col="dfs_keywords"    filters={filters} setF={setF} align="right" />
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Indexability</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {pages.isLoading && <tr><td colSpan={11} className="px-2 py-4 text-center text-muted-foreground">Loading…</td></tr>}
            {pages.data?.items.map((page, i) => <PageTableRow key={i} page={page} />)}
            {pages.data && pages.data.items.length === 0 && (
              <tr><td colSpan={11} className="px-2 py-4 text-center text-muted-foreground">
                No pages match the filters. Trigger a crawl from the Crawl tab if no data yet.
              </td></tr>
            )}
          </tbody>
        </table>

        {pages.data && pages.data.total > (filters.limit ?? 100) && (
          <div className="mt-3 flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              Page {Math.floor((filters.offset ?? 0) / (filters.limit ?? 100)) + 1} of{" "}
              {Math.ceil(pages.data.total / (filters.limit ?? 100))}
            </span>
            <div className="flex items-center gap-1">
              <button type="button"
                disabled={(filters.offset ?? 0) === 0}
                onClick={() => setFilters((f) => ({ ...f, offset: Math.max(0, (f.offset ?? 0) - (f.limit ?? 100)) }))}
                className="rounded-md border border-border bg-background px-2 py-1 hover:bg-accent disabled:opacity-50">Prev</button>
              <button type="button"
                disabled={(filters.offset ?? 0) + (filters.limit ?? 100) >= pages.data.total}
                onClick={() => setFilters((f) => ({ ...f, offset: (f.offset ?? 0) + (f.limit ?? 100) }))}
                className="rounded-md border border-border bg-background px-2 py-1 hover:bg-accent disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}


function PageTableRow({ page: p }: { page: PageRow }) {
  const [open, setOpen] = useState(false);
  const statusKind = !p.status_code ? undefined
                   : p.status_code >= 500 ? "err"
                   : p.status_code >= 400 ? "warn"
                   : p.status_code >= 300 ? "info"
                   : "ok";
  return (
    <>
      <tr className="cursor-pointer hover:bg-accent/40" onClick={() => setOpen((o) => !o)}>
        <td className="px-2 py-1.5 align-top font-mono text-[11px]">
          <a href={p.url} target="_blank" rel="noreferrer"
             onClick={(e) => e.stopPropagation()}
             className="inline-flex items-center gap-0.5 hover:underline" title={p.url}>
            <span className="truncate">{stripScheme(p.url)}</span>
            <ExternalLink className="h-3 w-3 opacity-50" />
          </a>
        </td>
        <td className="px-2 py-1.5 text-right align-top">
          {p.status_code != null && (
            <span className={cn(
              "rounded px-1 font-mono",
              statusKind === "ok"   && "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
              statusKind === "info" && "bg-blue-500/10 text-blue-700 dark:text-blue-400",
              statusKind === "warn" && "bg-amber-500/10 text-amber-700 dark:text-amber-400",
              statusKind === "err"  && "bg-red-500/10 text-red-700 dark:text-red-400",
            )}>{p.status_code}</span>
          )}
        </td>
        <td className="px-2 py-1.5 text-right align-top">{p.word_count?.toLocaleString() ?? "—"}</td>
        <td className="px-2 py-1.5 text-right align-top">
          {p.inlinks != null ? `${p.inlinks}/${p.outlinks ?? 0}` : "—"}
        </td>
        <td className="px-2 py-1.5 text-right align-top">
          {p.gsc_clicks != null && p.gsc_clicks > 0 ? p.gsc_clicks.toLocaleString() : "—"}
        </td>
        <td className="px-2 py-1.5 text-right align-top">
          {p.gsc_impressions != null && p.gsc_impressions > 0 ? p.gsc_impressions.toLocaleString() : "—"}
        </td>
        <td className="px-2 py-1.5 text-right align-top">
          {p.gsc_position != null && p.gsc_position > 0 ? p.gsc_position.toFixed(1) : "—"}
        </td>
        <td className="px-2 py-1.5 text-right align-top">
          {p.ga4_sessions != null && p.ga4_sessions > 0 ? p.ga4_sessions.toLocaleString() : "—"}
        </td>
        <td className="px-2 py-1.5 text-right align-top">
          {p.ga4_bounce_rate != null && p.ga4_bounce_rate > 0 ? `${(p.ga4_bounce_rate * 100).toFixed(0)}%` : "—"}
        </td>
        <td className="px-2 py-1.5 text-right align-top">
          {p.dfs_keywords ?? "—"}
        </td>
        <td className="px-2 py-1.5 align-top">
          <div className="text-[11px]">{p.indexability ?? "—"}</div>
          {p.indexability_status && (
            <div className="text-[10px] text-muted-foreground">{p.indexability_status}</div>
          )}
        </td>
      </tr>
      {open && (
        <tr className="bg-muted/20"><td colSpan={11} className="px-4 py-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <KV k="Title"            v={p.title ?? "(missing)"} mono />
            <KV k="Title length"     v={p.title_length?.toString() ?? "—"} />
            <KV k="Meta"             v={p.meta_description ?? "(missing)"} />
            <KV k="H1"               v={p.h1 ?? "—"} />
            <KV k="Crawl depth"      v={p.crawl_depth?.toString() ?? "—"} />
            <KV k="Canonical"        v={p.canonical_url ?? "—"} mono />
            <KV k="GA4 engagement"   v={p.ga4_engagement_rate ? `${(p.ga4_engagement_rate * 100).toFixed(1)}%` : "—"} />
            <KV k="DFS ETV"          v={p.dfs_etv ? Math.round(p.dfs_etv).toLocaleString() : "—"} />
            <KV k="DFS backlinks"    v={p.dfs_backlinks?.toString() ?? "—"} />
            <KV k="DFS ref domains"  v={p.dfs_referring_domains?.toString() ?? "—"} />
          </div>
        </td></tr>
      )}
    </>
  );
}


function SrcChip({ label, present }: { label: string; present: boolean }) {
  return (
    <span className={cn(
      "rounded px-2 py-0.5",
      present ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-muted text-muted-foreground line-through",
    )}>{label}</span>
  );
}

function FilterToggle({ label, value, onTrue, onFalse, onChange }: {
  label: string; value: boolean | undefined;
  onTrue: string; onFalse: string;
  onChange: (v: boolean | undefined) => void;
}) {
  const cycle = () => {
    if (value === undefined) onChange(true);
    else if (value === true) onChange(false);
    else onChange(undefined);
  };
  const display = value === undefined ? "any" : value === true ? onTrue : onFalse;
  return (
    <button type="button" onClick={cycle}
      className={cn(
        "rounded-md border px-2 py-1.5 text-xs",
        value === undefined ? "border-input bg-background" :
        value === true ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400" :
                         "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
      )}>{label}: {display}</button>
  );
}

function SortHeader({ label, col, filters, setF, align, className }: {
  label: string; col: string;
  filters: PagesLensFilters;
  setF: (patch: Partial<PagesLensFilters>) => void;
  align?: "right"; className?: string;
}) {
  const active = filters.sort === col;
  const dir = active ? filters.sort_dir : "asc";
  return (
    <th onClick={() => setF({ sort: col, sort_dir: active && dir === "desc" ? "asc" : "desc", offset: 0 })}
      className={cn(
        "cursor-pointer select-none px-2 py-1.5 font-medium text-muted-foreground hover:text-foreground",
        align === "right" && "text-right", className,
      )}>
      <span className="inline-flex items-center gap-0.5">
        {label}
        {active && (dir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
      </span>
    </th>
  );
}

function KV({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="text-xs">
      <span className="text-muted-foreground">{k}:</span>{" "}
      <span className={cn(mono && "font-mono")}>{v}</span>
    </div>
  );
}

function stripScheme(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/^www\./, "");
}
